import {FC, useRef} from "react"

import {InformationBanner} from "@frontend/components/ui/information-banner"
import {useTranslation} from "@frontend/i18n"
import {useEffectOnMount} from "@frontend/utils/useEffectOnMount"
import {css, cx} from "@styled-system/css"
import {hstack} from "@styled-system/patterns"

import {RecommendationBasedOn} from "./based-on"
import {Remediation} from "./remediation"
import {RemediationProps, nonRecommendedInset} from "./types"

const RECOMMENDED_CLASS = "recommended"

export const DesktopRemediations: FC<RemediationProps> = ({
  caseModelId,
  causeOnMessageId,
  openModal,
  isReplacePriceLoading,
  pageData: {
    recommendation,
    feedback,
    remediationData,
    type,
    productInfo,
    causes,
    parts,
    applianceAgeMonths,
    agentProfileId,
  },
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffectOnMount(() => {
    if (scrollRef.current) {
      const recommendedRemediation = scrollRef.current?.querySelector(
        `.${RECOMMENDED_CLASS}`,
      )

      recommendedRemediation?.scrollIntoView({
        behavior: "instant",
        block: "start",
        inline: "center",
      })
    }
  })

  const t = useTranslation()
  const showPartUnavailableBanner =
    recommendation?.reasons?.includes("partUnavailable") ?? false

  return (
    <div
      className={css({
        display: {
          base: "none",
          desktop: "flex",
          largeDesktop: "none",
        },
        flexDirection: "column",
        paddingTop: 20,
        maxWidth: "calc(100vw - 100px)",
        height: "100%",
      })}
    >
      {showPartUnavailableBanner && (
        <div
          className={css({
            marginBottom: 8,
          })}
        >
          <InformationBanner
            variant="warning"
            content={t("cause.nextStep.partUnavailable")}
          />
        </div>
      )}
      <div
        className={hstack({
          overflowX: "auto",
          flexWrap: "nowrap",
          flexShrink: 0,
          alignItems: "stretch",
          paddingBottom: 36,
          paddingX: 36,
          paddingTop: 16,
          gap: 16,
          desktopDown: {
            flexDirection: "column",
          },
          "& > div": {
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            minWidth: 265,
          },
        })}
        ref={scrollRef}
      >
        {remediationData.map((data) => (
          <Remediation
            key={data.type}
            caseId={caseModelId}
            causeOnMessageId={causeOnMessageId}
            data={data}
            productInfo={productInfo}
            causes={causes}
            parts={parts}
            applianceAgeMonths={applianceAgeMonths}
            agentProfileId={agentProfileId}
            isReplacePriceLoading={isReplacePriceLoading}
            wrapperClassName={
              data.isRecommended ? undefined : nonRecommendedInset
            }
            className={cx(
              css(
                {
                  gridColumn: "span 3",
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: 0,
                  height: "100%",
                },
                !data.isRecommended && {minHeight: 470},
              ),
              data.isRecommended && RECOMMENDED_CLASS,
            )}
          />
        ))}
      </div>
      {type.type === "remediation" && recommendation && (
        <div
          className={css({
            marginTop: "auto",
            paddingX: 36,
            paddingBottom: 8,
          })}
        >
          <RecommendationBasedOn
            recommendation={recommendation}
            feedback={feedback.RECOMMENDATION}
            caseId={caseModelId}
            type={type}
            openModal={openModal}
          />
        </div>
      )}
    </div>
  )
}
